<script setup>
import Modal from "@/components/modal/Modal";
import * as yup from "yup";
import {MedicalFacilityListQuery} from "@/graphql/queries/medicalFacilityList.graphql";
import {useMutation, useQuery} from "@vue/apollo-composable";
import {HpoHospitalCapacityMutation, HpoHospitalCreateMutation} from "@/graphql/mutations/hpo/hospital.graphql"
import {computed, ref} from "vue"
import {useLastResult} from "@/graphql/composable";
import {numberValidator, requiredMessage, stringValidator} from "../../../../utils/yupValidators";
import {useDjangoForm} from "@/utils/forms";
import NumberSplitInput from "@/components/forms/NumberSplitInput.vue";


const props = defineProps({
    hpoId: {
        type: String,
        require: true,
    },
    hospital: {
        type: Object,
        required: true,
    },
})
const emit = defineEmits(['close'])

const {
    result: hospitalListResult,
    loading: hospitalListLoading,
} = useQuery(MedicalFacilityListQuery)
const hospitalList = useLastResult(hospitalListResult, [], data => data.medicalFacilityList.map(item => item.name))

const {mutate: hpoHospitalCreateMutation} = useMutation(HpoHospitalCreateMutation);
const {mutate: hpoHospitalCapacityMutation} = useMutation(HpoHospitalCapacityMutation);

const titleText = computed(() => {
    let text = pgettext('label', 'Hospital Capacity')
    if (props.hospital.id) {
        text = text + ': ' + props.hospital.name
    }
    return text
})

const {defineField, handleMutationSubmit, nonFieldErrors} = useDjangoForm({
    validationSchema: yup.object({
        name: stringValidator.required(requiredMessage),
        red: numberValidator.required(requiredMessage),
        yellow: numberValidator.required(requiredMessage),
        green: numberValidator.required(requiredMessage),
    }),
    initialValues: {
        name: props.hospital.name,
        red: props.hospital.red,
        green: props.hospital.green,
        yellow: props.hospital.yellow,
    }
});

const [name, nameProps] = defineField('name');
const [red, redProps] = defineField('red');
const [yellow, yellowProps] = defineField('yellow');
const [green, greenProps] = defineField('green');

const onSubmit = handleMutationSubmit(
    (values) => {
        if (props.hospital.id) {
            return changeHospitalCapacity(values);
        } else {
            return createHospital(values);
        }
    },
    {
        onSuccess(result) {
            close();
        }
    }
);

function createHospital(values) {
    // const name = this.formData.medicalFacilityName ? this.formData.medicalFacilityName : this.initialValues.medicalFacilityNameHpoHospital
    return hpoHospitalCreateMutation({
        hpo: props.hpoId,
        name: values.name,
        red: values.red,
        yellow: values.yellow,
        green: values.green,
    });
}

function changeHospitalCapacity(values) {
    return hpoHospitalCapacityMutation({
        id: props.hospital.id,
        red: values.red,
        yellow: values.yellow,
        green: values.green,
    });
}

function close() {
    emit('close')
}
</script>

<template>
    <Modal :max-width="640" :title="titleText" @close="close">
        <v-form @submit.prevent="onSubmit">
            <v-row>
                <v-col cols="12">
                    <v-alert v-for="message in nonFieldErrors" :title="message" type="error"></v-alert>
                    <!-- Destination Hospital -->
                    <v-combobox
                        v-model="name"
                        v-bind="nameProps"
                        :label="pgettext('label', 'Hospital Name')"
                        :items="hospitalList"
                        :loading="hospitalListLoading"
                        class="mb-2"
                        item-title="name"
                        item-value="name"
                    ></v-combobox>

                    <!-- Red -->
                    <NumberSplitInput v-model="red" v-bind="redProps"
                                      class="mb-2"
                                      :label="pgettext('hospital capacity form label', 'red')"
                                      :min="0"/>

                    <!-- Yellow -->
                    <NumberSplitInput v-model="yellow" v-bind="yellowProps"
                                      class="mb-2"
                                      :label="pgettext('hospital capacity form label', 'yellow')"
                                      :min="0"
                                      control-variant="split"/>

                    <!-- Green -->
                    <NumberSplitInput v-model="green" v-bind="greenProps"
                                      class="mb-2"
                                      :label="pgettext('hospital capacity form label', 'green')"
                                      :min="0"
                                      control-variant="split"/>

                    <v-btn :text="pgettext('action', 'Save')"
                           type="submit"
                           block
                    ></v-btn>
                </v-col>
            </v-row>
        </v-form>
    </Modal>
</template>